import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import AuthGuard from "auth-guard";
import Login from "components/login/Login";

// import SelfRegistration from "components/SelfRegistration/SelfRegistration";
// import GlobalRegistration from "components/GlobalRegistration/GlobalRegistration";
import PrintIdCard from "components/events/eventSessions/menuItems/PrintIdCard";
import Launch from "components/mobile/Launch/index";
import Refresh from "components/mobile/Launch/refresh";
import DownloadQr from "sharedComponents/downloadQr";
import LaunchDev from "components/mobile/Launch/LaunchDev";
import HFNErrorBoundary from "components/errorBoundary";

// shared components
import HFNLoader from "sharedComponents/lazyLoading";
import HFNToaster from "sharedComponents/toasters";
import HFNConfirmDialog from "sharedComponents/confirmDialog";

// utils
import { lazy } from "utils/lazy";

import {
  getLoginLocaluserData,
  isLoginAuth,
  logout,
  setLoginData,
} from "utils/login";

// config
import config from "assets/config";
import SelfAttendance from "components/SelfAttendance";
import HFNAuth from "components/hfnauth";
import { UNAUTH_URLS } from "utils/constants";
import {
  getHomePath,
  getAppMode,
  isWebsiteUnderMaintenance,
  setPWAInstallPrompt,
} from "utils/common";
// eslint-disable-next-line import/no-unresolved
import "@hfn-uicore/core/css";

// lazy components
const LayoutContainer = lazy("layouts/Template");
const MobileLayoutContainer = lazy("layouts/PlainTemplate");
const PwaLayoutContainer = lazy("layouts/PwaTemplate");
const UepLayoutTemplate = lazy("layouts/UepTemplate");
const HfnAppContainer = lazy("layouts/HfnAppTemplate");
const SelfRegistration = lazy("SelfRegistration/SelfRegistration");
const GlobalRegistration = lazy("GlobalRegistration/GlobalRegistration");
const DownloadPWA = lazy("downloadApp/index");

function App() {
  const params = JSON.parse(config?.keycloakAuthConfig);
  const [underMaintenance, setUnderMaintenance] = useState(false);
  const { pathname } = useLocation();
  const elementRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    isWebsiteUnderMaintenance().then((res) => setUnderMaintenance(res));
  }, []);

  useEffect(() => {
    if (underMaintenance) {
      import("hfn-maintenance-web/main");
    }
  }, [underMaintenance]);

  useEffect(() => {
    import("hfn-appmode/main");
  }, []);

  useEffect(() => {
    if (pathname.includes("mobile")) {
      return;
    }
    const lsData = getLoginLocaluserData();
    if (
      lsData &&
      typeof lsData === "object" &&
      Object.keys(lsData).length > 0
    ) {
      setLoginData(lsData);
    }
    if (
      isLoginAuth() &&
      (pathname.includes("hfnauth") || pathname.includes("login"))
    ) {
      const redirectTo = getHomePath();
      navigate(redirectTo);
    }
  }, []);
  const handleUserLoggedOut = async (event) => {
    if (event?.detail?.loggedOut) await logout();
  };
  useEffect(() => {
    // Register the Service Worker after a short delay
    const swTimeout = setTimeout(() => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker
          .register("/service-worker.js")
          .then((registration) => {
            registration.update(); // Check for updates
          })
          .catch(() => {
            // console.error("Service Worker registration failed:", error);
          });

        // Refresh the page when the new service worker takes control
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          window.location.reload();
        });
      } else {
        // console.log("Service Workers not supported in this browser");
      }
    }, 300);

    // To prevent the automatic PWA install prompt and saves the event for triggering it later manually.
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setPWAInstallPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Assign and listen to element event
    elementRef.current = document.querySelector("hfn-auth");
    if (elementRef.current) {
      elementRef.current.addEventListener("userLoggedOut", handleUserLoggedOut);
    }

    // Cleanup event listener on unmount or when the path changes
    return () => {
      clearTimeout(swTimeout);
      if (elementRef.current) {
        elementRef.current.removeEventListener(
          "userLoggedOut",
          handleUserLoggedOut
        );
      }
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, [elementRef]);

  // Listens for chunk load errors, clears all service worker caches, and reloads the page to fix broken PWA updates.
  useEffect(() => {
    const handleChunkError = (e) => {
      if (e.message && e.message.includes("ChunkLoadError")) {
        caches
          .keys()
          .then((names) =>
            Promise.all(names.map((name) => caches.delete(name)))
          )
          .finally(() => {
            window.location.reload(true);
          });
      }
    };

    window.addEventListener("error", handleChunkError);
    return () => window.removeEventListener("error", handleChunkError);
  }, []);

  return (
    <HFNErrorBoundary>
      <HFNLoader>
        <hfn-auth
          ref={elementRef}
          config={JSON.stringify(params)}
          showCancel
          authType={
            UNAUTH_URLS.some((path) => pathname.includes(path))
              ? "on-demand"
              : ""
          }
        />
        <div className="hfn">
          <hfn-app-mode
            appMode={getAppMode(process.env.REACT_APP_APP_MODE)}
            displayOn="topMiddle"
          />

          {underMaintenance ? (
            <hfn-Maintenance-web />
          ) : (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/hfnauth/authorization" element={<HFNAuth />} />
              <Route
                path="/self-registration/:event/:session"
                element={<SelfRegistration />}
              />
              <Route path="/download-app" element={<DownloadPWA />} />
              <Route
                path="/print-qr/:eventId/:sessionId/:blockId/:source/:type"
                element={<DownloadQr />}
              />
              <Route
                path="/global-event-registration"
                element={<GlobalRegistration />}
              />
              <Route
                path="/global-event-registration-non-connect"
                element={<GlobalRegistration />}
              />
              <Route
                path="/non-connect-self-attendance/:eventId/:sessionId"
                element={<SelfAttendance />}
              />
              <Route
                path="/idcard-print/:cardNumber"
                element={<PrintIdCard />}
              />
              <Route
                path="/idcard-print/:event/:cardNumber"
                element={<PrintIdCard />}
              />
              <Route path="mobile/launch" element={<Launch />} />
              <Route path="mobile/launch/refresh" element={<Refresh />} />
              <Route path="mobile/launch-dev" element={<LaunchDev />} />
              <Route path="/mobile/*" element={<MobileLayoutContainer />} />
              <Route path="/pwa/*" element={<PwaLayoutContainer />} />
              <Route path="/heartfulness-app/*" element={<HfnAppContainer />} />
              <Route path="/users/*" element={<UepLayoutTemplate />} />
              <Route
                path="*"
                element={<AuthGuard path="/" component={LayoutContainer} />}
              />
            </Routes>
          )}
        </div>

        <div>
          <HFNToaster />
          <HFNConfirmDialog />
        </div>
      </HFNLoader>
    </HFNErrorBoundary>
  );
}

export default App;
